import { useState, useEffect, useCallback, useRef } from 'react'
import api from '../api/client'
import useDebounce from './useDebounce'

export function useAbm(endpoint, options = {}) {
  const { paginated = false, perPage = 50, params: extraParams = null } = options
  // Params fijos del listado (ej: ?inactivos=1). Se serializan para poder
  // usarlos como dependencia: un objeto literal cambia de identidad en cada
  // render y dispararía un fetch infinito.
  const extraKey = JSON.stringify(extraParams ?? null)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [page, setPage] = useState(1)
  const [search, setSearchValue] = useState('')
  const [meta, setMeta] = useState(null)
  const [colFilters, setColFilters] = useState({})
  // El buscador principal disparaba un fetch POR TECLA (auditoría 1 jul 2026):
  // el input usa `search` vivo, el fetch usa el valor debounced.
  const busqueda = useDebounce(search, 350)
  const hayDatos = useRef(false)

  // cambiar la búsqueda siempre vuelve a la primera página
  const setSearch = useCallback((value) => {
    setSearchValue(value)
    setPage(1)
  }, [])

  // filtro por columna (server-side): f[campo]=valor
  const setColFilter = useCallback((key, value) => {
    setColFilters(prev => {
      const next = { ...prev }
      if (value === '' || value == null) delete next[key]
      else next[key] = value
      return next
    })
    setPage(1)
  }, [])

  const fetch = useCallback(async () => {
    // Keep-previous-data: "Cargando..." solo la primera vez; después la tabla
    // queda visible mientras llega lo nuevo (sin blanqueo por cada filtro/tecla).
    if (!hayDatos.current) {
      setLoading(true)
    }
    try {
      const params = {
        ...(paginated
          ? { page, per_page: perPage, ...(busqueda ? { q: busqueda } : {}), ...(Object.keys(colFilters).length ? { f: colFilters } : {}) }
          : {}),
        ...(JSON.parse(extraKey) ?? {}),
      }
      const { data } = await api.get(`/${endpoint}`, { params })
      hayDatos.current = true
      setRows(Array.isArray(data) ? data : (data.data ?? []))
      if (!Array.isArray(data) && data.current_page) {
        setMeta({
          current_page: data.current_page,
          last_page: data.last_page,
          total: data.total,
          per_page: data.per_page,
          from: data.from,
          to: data.to,
        })
      } else {
        setMeta(null)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cargar datos')
    } finally {
      setLoading(false)
    }
  }, [endpoint, paginated, page, perPage, busqueda, colFilters, extraKey])

  useEffect(() => { fetch() }, [fetch])

  const create = async (payload) => {
    const { data } = await api.post(`/${endpoint}`, payload)
    if (paginated) { await fetch() } else { setRows(prev => [data, ...prev]) }
    return data
  }

  const update = async (id, payload) => {
    const { data } = await api.put(`/${endpoint}/${id}`, payload)
    setRows(prev => prev.map(r => r.id === id ? data : r))
    return data
  }

  const remove = async (row) => {
    await api.delete(`/${endpoint}/${row.id}`)
    if (paginated) { await fetch() } else { setRows(prev => prev.filter(r => r.id !== row.id)) }
  }

  return { rows, loading, error, create, update, remove, refresh: fetch, page, setPage, search, setSearch, meta, colFilters, setColFilter }
}
